import { defineStore, acceptHMRUpdate } from 'pinia';
import { Article } from 'typed-repository';
import type { ArticleModel } from 'data-types-config';
import { logger } from '../utils/logger';

interface ArticlesStoreState {
  articles: ArticleModel[];
  cache: Record<string, ArticleModel>;
  isLoading: boolean;
  currentPage: number;
  totalPages: number;
  hideLoadMore: boolean;
}

export const useArticlesStore = defineStore('ArticlesStore', {
  state: (): ArticlesStoreState => ({
    articles: [],
    cache: {},
    isLoading: false,
    currentPage: 1,
    totalPages: 1,
    hideLoadMore: false,
  }),

  actions: {
    addToCache(newArticles: ArticleModel[]) {
      newArticles.forEach((article) => {
        if (article.slug) {
          this.cache[article.slug] = JSON.parse(JSON.stringify(article));
        }
      });
    },

    async fetchArticles(perPage = 9) {
      if (this.currentPage === 1 && this.articles.length > 0) {
        return { data: this.articles, totalPages: this.totalPages };
      }

      if (this.currentPage > this.totalPages && this.articles.length > 0) {
        this.hideLoadMore = true;
        return;
      }

      this.isLoading = true;
      try {
        const articlesData = await $fetch<{
          data: ArticleModel[];
          totalPages: number;
        }>('/api/articles/articles', {
          params: {
            categoryKey: 'general',
            per_page: perPage,
            page: this.currentPage,
          },
        });

        const rawArticles = articlesData?.data ?? [];

        this.addToCache(rawArticles);

        const sanitizedArticles = JSON.parse(JSON.stringify(rawArticles));

        if (this.currentPage === 1) {
          this.articles = sanitizedArticles;
        } else {
          const existingIds = new Set(this.articles.map((a) => a.id));
          const filteredNew = sanitizedArticles.filter(
            (a: ArticleModel) => !existingIds.has(a.id),
          );
          this.articles = [...this.articles, ...filteredNew];
        }

        this.totalPages = articlesData?.totalPages ?? 1;
        this.hideLoadMore = this.currentPage >= this.totalPages;

        return articlesData;
      } catch (error) {
        this.handleError('Error fetching articles:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async loadMoreArticles() {
      this.currentPage++;
      await this.fetchArticles();
    },

    addArticles(newArticles: ArticleModel[]) {
      this.addToCache(newArticles);

      if (this.articles.length === 0) {
        this.articles = JSON.parse(JSON.stringify(newArticles));
      }
    },

    handleError(message: string, error: unknown) {
      logger.error(message, error);
    },
  },

  getters: {
    getArticles: (state) => state.articles,
    getArticlesCache: (state) => state.cache,

    getRichArticles: (state) => {
      return state.articles.map((a) => new Article(a));
    },
    getLatestArticles: (state) => (count: number) => {
      return state.articles.slice(0, count);
    },
    getLatestRichArticles: (state) => (count: number) => {
      return state.articles.slice(0, count).map((a) => new Article(a));
    },
    getArticleBySlug: (state) => (slug: string) => {
      const cached = state.cache[slug];
      if (!cached) return null;
      return new Article(cached);
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useArticlesStore, import.meta.hot));
}
